// src/components/SchoolDashboard.jsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaArrowRight,
  FaBookOpen,
  FaBullhorn,
  FaBuilding,
  FaCalendarAlt,
  FaChartLine,
  FaChalkboardTeacher,
  FaCheckCircle,
  FaClipboardList,
  FaCog,
  FaExclamationTriangle,
  FaMoneyBillWave,
  FaMoon,
  FaSchool,
  FaSun,
  FaUserGraduate,
  FaUsers,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { useAppTheme } from "../context/AppThemeContext";

const defaultPrefs = {
  showMetrics: true,
  showQuickActions: true,
  showAdmissionsBoard: true,
  showAcademicSnapshot: true,
  showFinanceSnapshot: true,
  showOperationsBoard: true,
  showCampusAlerts: true,
};

const metricCards = [
  {
    title: "Teaching Staff",
    value: "34",
    trend: "2 new onboarding tasks",
    icon: FaChalkboardTeacher,
    tone: "from-sky-500 to-cyan-500",
  },
  {
    title: "Active Students",
    value: "820",
    trend: "96% attendance this week",
    icon: FaUserGraduate,
    tone: "from-emerald-500 to-teal-500",
  },
  {
    title: "Pending Admissions",
    value: "12",
    trend: "5 require document review",
    icon: FaClipboardList,
    tone: "from-amber-500 to-orange-500",
  },
  {
    title: "Fee Collection",
    value: "91%",
    trend: "On track for this cycle",
    icon: FaMoneyBillWave,
    tone: "from-rose-500 to-pink-500",
  },
];

const quickActions = [
  {
    title: "Review Admissions",
    description: "Open pending applications and clear the review queue.",
    path: "/admin/admissions",
    icon: FaClipboardList,
    tone: "bg-sky-50 text-sky-700 border-sky-200",
  },
  {
    title: "Student Records",
    description: "Inspect admissions, documents, and current enrollments.",
    path: "/admission-details",
    icon: FaUsers,
    tone: "bg-emerald-50 text-emerald-700 border-emerald-200",
  },
  {
    title: "Account & Security",
    description: "Manage school profile details and password controls.",
    path: "/profile",
    icon: FaCog,
    tone: "bg-amber-50 text-amber-700 border-amber-200",
  },
  {
    title: "School Settings",
    description: "Adjust academic operations, notices, and session planning.",
    path: null,
    icon: FaBuilding,
    tone: "bg-violet-50 text-violet-700 border-violet-200",
  },
];

const admissionsBoardSeed = [
  {
    id: "ADM-611",
    name: "Anaya Kulkarni",
    standard: "Class 6",
    status: "Documents pending",
    tone: "bg-amber-100 text-amber-700",
  },
  {
    id: "ADM-612",
    name: "Dev Malhotra",
    standard: "Class 9",
    status: "Ready for approval",
    tone: "bg-emerald-100 text-emerald-700",
  },
  {
    id: "ADM-613",
    name: "Sara Dsouza",
    standard: "Class 11",
    status: "Fee follow-up",
    tone: "bg-rose-100 text-rose-700",
  },
];

const campusAlertsSeed = [
  {
    id: 1,
    title: "Bus route change awaiting notice",
    detail: "Parents of route 3 students should receive an update before 5 PM.",
    level: "alert",
  },
  {
    id: 2,
    title: "Library stock sync completed",
    detail: "The weekly catalog sync finished successfully 20 minutes ago.",
    level: "success",
  },
  {
    id: 3,
    title: "Science lab maintenance scheduled",
    detail: "Lab 2 will be unavailable on Saturday morning.",
    level: "info",
  },
];

const operationsSeed = [
  {
    id: 1,
    title: "Approve transport roster",
    detail: "Morning and evening route assignments need confirmation.",
    priority: "High",
  },
  {
    id: 2,
    title: "Publish weekly circular",
    detail: "Share exam preparation guidance and parent meeting schedule.",
    priority: "Medium",
  },
  {
    id: 3,
    title: "Verify teacher timetable swaps",
    detail: "Two substitutions were requested for the next working day.",
    priority: "Medium",
  },
];

const academicHighlights = [
  { label: "Classes Running", value: "42", note: "Across primary to senior secondary" },
  { label: "Avg Attendance", value: "94%", note: "Across all sections this week" },
  { label: "Exam Readiness", value: "87%", note: "Invigilation and halls assigned" },
];

const preferenceOptions = [
  { key: "showMetrics", label: "Top metrics" },
  { key: "showQuickActions", label: "Quick actions" },
  { key: "showAdmissionsBoard", label: "Admissions board" },
  { key: "showAcademicSnapshot", label: "Academic snapshot" },
  { key: "showFinanceSnapshot", label: "Finance snapshot" },
  { key: "showOperationsBoard", label: "Operations board" },
  { key: "showCampusAlerts", label: "Campus alerts" },
];

const loadPrefs = () => {
  try {
    const stored = localStorage.getItem("schoolDashboardPrefs");
    if (!stored) return defaultPrefs;
    const parsed = JSON.parse(stored);
    return { ...defaultPrefs, ...parsed };
  } catch {
    return defaultPrefs;
  }
};

const getAlertTone = (level, isDarkMode) => {
  if (level === "success") {
    return isDarkMode
      ? "border-emerald-900/60 bg-emerald-950/40"
      : "border-emerald-200 bg-emerald-50";
  }
  if (level === "alert") {
    return isDarkMode
      ? "border-amber-900/60 bg-amber-950/40"
      : "border-amber-200 bg-amber-50";
  }
  return isDarkMode
    ? "border-slate-800 bg-slate-900/70"
    : "border-slate-200 bg-slate-50";
};

const SchoolDashboard = () => {
  const { theme, isDarkMode, toggleTheme } = useAppTheme();
  const [prefs, setPrefs] = useState(loadPrefs);
  const [draftPrefs, setDraftPrefs] = useState(loadPrefs);
  const [showCustomize, setShowCustomize] = useState(false);
  const [alerts, setAlerts] = useState(campusAlertsSeed);
  const [operations, setOperations] = useState(operationsSeed);
  const rawUserName = localStorage.getItem("currentUserName") || "";
  const displayName = rawUserName
    ? rawUserName
        .split("@")[0]
        .replace(/[._-]+/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase())
    : "School Team";
  const greeting = (() => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) return "Good morning";
    if (hour >= 12 && hour < 17) return "Good afternoon";
    if (hour >= 17 && hour < 21) return "Good evening";
    return "Hello";
  })();

  useEffect(() => {
    localStorage.setItem("schoolDashboardPrefs", JSON.stringify(prefs));
  }, [prefs]);

  useEffect(() => {
    if (showCustomize) {
      setDraftPrefs(prefs);
    }
  }, [showCustomize, prefs]);

  const toggleDraftPref = (key) => {
    setDraftPrefs((current) => ({ ...current, [key]: !current[key] }));
  };

  const applyCustomization = () => {
    setPrefs(draftPrefs);
    setShowCustomize(false);
    toast.success("School dashboard updated.");
  };

  const resetCustomization = () => {
    setDraftPrefs(defaultPrefs);
  };

  const dismissAlert = (id) => {
    setAlerts((current) => current.filter((item) => item.id !== id));
  };

  const completeOperation = (id) => {
    const task = operations.find((item) => item.id === id);
    setOperations((current) => current.filter((item) => item.id !== id));
    if (task) {
      toast.success(`Completed: ${task.title}`);
    }
  };

  const handleQuickAction = (title) => {
    toast.info(`${title} is ready for backend wiring.`);
  };

  return (
    <div
      className={`min-h-screen px-3 py-4 md:px-6 md:py-8 ${
        theme === "dark"
          ? "bg-slate-950 text-slate-100"
          : "bg-[radial-gradient(circle_at_top,_rgba(34,197,94,0.12),_transparent_26%),linear-gradient(180deg,_#f8fafc_0%,_#fffdf7_48%,_#eff6ff_100%)] text-slate-900"
      }`}
    >
      <div className="mx-auto max-w-7xl space-y-6">
        <section
          className={`overflow-hidden rounded-3xl border px-4 py-5 shadow-2xl sm:px-6 sm:py-8 md:px-8 ${
            isDarkMode
              ? "border-slate-800 bg-gradient-to-br from-slate-900 via-slate-900 to-emerald-950 shadow-black/30"
              : "border-white/60 bg-gradient-to-br from-slate-900 via-emerald-900 to-cyan-900 text-white shadow-slate-300/40"
          }`}
        >
          <div className="grid gap-6 lg:grid-cols-[1.15fr_0.85fr]">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.28em] text-emerald-200">
                School Operations Center
              </p>
              <h1 className="mt-3 text-2xl font-extrabold sm:text-3xl md:text-5xl">
                {greeting}, {displayName}
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-emerald-50/90 md:text-base">
                Run admissions, staffing, academic planning, and campus coordination from one workspace designed for school-level oversight.
              </p>
              <div className="mt-6 overflow-x-auto pb-1">
                <div className="flex min-w-max gap-3">
                <span className="rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm font-semibold">
                  12 admissions pending
                </span>
                <span className="rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm font-semibold">
                  3 campus alerts
                </span>
                <span className="rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm font-semibold">
                  42 active classes
                </span>
                </div>
              </div>
              <div className="mt-6 grid gap-3 sm:flex sm:flex-wrap">
                <button
                  type="button"
                  onClick={toggleTheme}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/15 sm:w-auto"
                >
                  {isDarkMode ? <FaSun /> : <FaMoon />}
                  {isDarkMode ? "Light mode" : "Dark mode"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowCustomize(true)}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/15 sm:w-auto"
                >
                  <FaCog /> Customize dashboard
                </button>
                <Link
                  to="/admin/admissions"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-emerald-300 px-4 py-2 text-sm font-semibold text-slate-950 transition hover:bg-emerald-200 sm:w-auto"
                >
                  Open admissions <FaArrowRight className="text-xs" />
                </Link>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2 sm:gap-4">
              <div className="rounded-2xl border border-white/10 bg-white/10 p-5 backdrop-blur-sm">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-100">Today&apos;s Priority</p>
                <p className="mt-3 text-xl font-bold text-white">Admission clearance and staffing</p>
                <p className="mt-2 text-sm text-emerald-50/90">
                  Push urgent approvals, close pending teacher assignments, and publish transport updates.
                </p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/10 p-5 backdrop-blur-sm">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-100">Campus Status</p>
                <p className="mt-3 text-xl font-bold text-white">Stable and operational</p>
                <p className="mt-2 text-sm text-emerald-50/90">
                  Finance sync, academic planning, and notice distribution are functioning normally.
                </p>
              </div>
            </div>
          </div>
        </section>

        {prefs.showMetrics && (
          <section className="grid grid-cols-2 gap-3 sm:gap-5 xl:grid-cols-4">
            {metricCards.map((card) => {
              const Icon = card.icon;
              return (
                <div
                  key={card.title}
                  className={`rounded-3xl border p-4 shadow-lg transition-all duration-300 hover:-translate-y-1 sm:p-6 ${
                    isDarkMode
                      ? "border-slate-800 bg-slate-900 shadow-black/20"
                      : "border-slate-200 bg-white shadow-slate-200/60"
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                        {card.title}
                      </p>
                      <p className="mt-3 text-2xl font-extrabold sm:text-3xl">{card.value}</p>
                      <p className={`mt-2 text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                        {card.trend}
                      </p>
                    </div>
                    <div className={`flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br ${card.tone} text-white shadow-lg sm:h-14 sm:w-14`}>
                      <Icon className="text-xl" />
                    </div>
                  </div>
                </div>
              );
            })}
          </section>
        )}

        <section className="grid gap-6 lg:grid-cols-2 xl:grid-cols-[1.15fr_0.85fr]">
          <div className="space-y-6">
            {prefs.showQuickActions && (
              <div className={`rounded-3xl border p-6 shadow-lg ${isDarkMode ? "border-slate-800 bg-slate-900 shadow-black/20" : "border-slate-200 bg-white shadow-slate-200/60"}`}>
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Quick Actions</p>
                    <h2 className="mt-2 text-2xl font-bold">School-wide shortcuts</h2>
                  </div>
                  <div className={`rounded-full px-3 py-1 text-xs font-semibold ${isDarkMode ? "bg-slate-800 text-slate-300" : "bg-slate-100 text-slate-600"}`}>
                    School role
                  </div>
                </div>
                <div className="mt-6 grid gap-4 sm:grid-cols-2">
                  {quickActions.map((action) => {
                    const Icon = action.icon;
                    if (action.path) {
                      return (
                        <Link
                          key={action.title}
                          to={action.path}
                          className={`group rounded-2xl border p-4 transition-all duration-300 hover:-translate-y-1 sm:p-5 ${isDarkMode ? "border-slate-800 bg-slate-950 hover:border-slate-700" : "border-slate-200 bg-slate-50 hover:border-slate-300 hover:bg-white"}`}
                        >
                          <div className={`inline-flex h-12 w-12 items-center justify-center rounded-2xl ${action.tone}`}>
                            <Icon className="text-lg" />
                          </div>
                          <h3 className="mt-4 text-lg font-bold">{action.title}</h3>
                          <p className={`mt-2 text-sm leading-6 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                            {action.description}
                          </p>
                          <div className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-emerald-500 transition-all duration-300 group-hover:translate-x-1">
                            Open <FaArrowRight className="text-xs" />
                          </div>
                        </Link>
                      );
                    }
                    return (
                      <button
                        key={action.title}
                        type="button"
                        onClick={() => handleQuickAction(action.title)}
                        className={`group rounded-2xl border p-4 text-left transition-all duration-300 hover:-translate-y-1 sm:p-5 ${isDarkMode ? "border-slate-800 bg-slate-950 hover:border-slate-700" : "border-slate-200 bg-slate-50 hover:border-slate-300 hover:bg-white"}`}
                      >
                        <div className={`inline-flex h-12 w-12 items-center justify-center rounded-2xl ${action.tone}`}>
                          <Icon className="text-lg" />
                        </div>
                        <h3 className="mt-4 text-lg font-bold">{action.title}</h3>
                        <p className={`mt-2 text-sm leading-6 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                          {action.description}
                        </p>
                        <div className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-emerald-500 transition-all duration-300 group-hover:translate-x-1">
                          Configure <FaArrowRight className="text-xs" />
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {prefs.showAdmissionsBoard && (
              <div className={`rounded-3xl border p-6 shadow-lg ${isDarkMode ? "border-slate-800 bg-slate-900 shadow-black/20" : "border-slate-200 bg-white shadow-slate-200/60"}`}>
                <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Admissions Board</p>
                    <h2 className="mt-2 text-2xl font-bold">Current school intake queue</h2>
                  </div>
                  <Link
                    to="/admin/admissions"
                    className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition ${isDarkMode ? "bg-slate-800 text-slate-200 hover:bg-slate-700" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
                  >
                    View all admissions <FaArrowRight className="text-xs" />
                  </Link>
                </div>

                <div className="mt-5 space-y-4">
                  {admissionsBoardSeed.map((item) => (
                    <div
                      key={item.id}
                      className={`flex flex-col gap-4 rounded-2xl border p-4 md:flex-row md:items-center md:justify-between ${isDarkMode ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-slate-50"}`}
                    >
                      <div>
                        <div className="flex items-center gap-3">
                          <p className="text-lg font-bold">{item.name}</p>
                          <span className={`rounded-full px-3 py-1 text-xs font-semibold ${item.tone}`}>
                            {item.status}
                          </span>
                        </div>
                        <p className={`mt-1 text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                          {item.id} • {item.standard}
                        </p>
                      </div>
                      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                        <Link
                          to="/admin/admissions"
                          className={`rounded-full px-4 py-2 text-center text-sm font-semibold transition ${isDarkMode ? "bg-slate-800 text-slate-200 hover:bg-slate-700" : "bg-white text-slate-700 hover:bg-slate-100"}`}
                        >
                          Review
                        </Link>
                        <button
                          type="button"
                          onClick={() => toast.success(`Marked ${item.name} for follow-up.`)}
                          className="rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700"
                        >
                          Follow up
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {prefs.showAcademicSnapshot && (
              <div className={`rounded-3xl border p-6 shadow-lg ${isDarkMode ? "border-slate-800 bg-slate-900 shadow-black/20" : "border-slate-200 bg-white shadow-slate-200/60"}`}>
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Academic Snapshot</p>
                    <h2 className="mt-2 text-2xl font-bold">Teaching and learning pulse</h2>
                  </div>
                  <div className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ${isDarkMode ? "bg-slate-800 text-slate-300" : "bg-emerald-50 text-emerald-700"}`}>
                    <FaBookOpen /> Weekly overview
                  </div>
                </div>

                <div className="mt-5 grid gap-4 md:grid-cols-3">
                  {academicHighlights.map((item) => (
                    <div key={item.label} className={`rounded-2xl p-4 ${isDarkMode ? "bg-slate-950" : "bg-slate-50"}`}>
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">{item.label}</p>
                      <p className={`mt-3 text-2xl font-extrabold ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>{item.value}</p>
                      <p className={`mt-2 text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>{item.note}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="space-y-6">
            {prefs.showCampusAlerts && (
              <div className={`rounded-3xl border p-6 shadow-lg ${isDarkMode ? "border-slate-800 bg-slate-900 shadow-black/20" : "border-slate-200 bg-white shadow-slate-200/60"}`}>
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Campus Alerts</p>
                    <h2 className="mt-2 text-2xl font-bold">What needs attention</h2>
                  </div>
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600">
                    <FaBullhorn />
                  </div>
                </div>

                <div className="mt-5 space-y-4">
                  {alerts.length === 0 ? (
                    <div className={`rounded-2xl border p-4 text-sm ${isDarkMode ? "border-slate-800 bg-slate-950 text-slate-400" : "border-slate-200 bg-slate-50 text-slate-500"}`}>
                      No active campus alerts.
                    </div>
                  ) : (
                    alerts.map((item) => (
                      <div key={item.id} className={`rounded-2xl border p-4 ${getAlertTone(item.level, isDarkMode)}`}>
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className="font-bold">{item.title}</p>
                            <p className={`mt-1 text-sm ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>
                              {item.detail}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => dismissAlert(item.id)}
                            className={`rounded-full px-3 py-1 text-xs font-semibold transition ${isDarkMode ? "bg-slate-800 text-slate-200 hover:bg-slate-700" : "bg-white text-slate-600 hover:bg-slate-100"}`}
                          >
                            Dismiss
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {prefs.showFinanceSnapshot && (
              <div className={`rounded-3xl border p-6 shadow-lg ${isDarkMode ? "border-slate-800 bg-slate-900 shadow-black/20" : "border-slate-200 bg-white shadow-slate-200/60"}`}>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Finance Snapshot</p>
                <h2 className="mt-2 text-2xl font-bold">Collections and dues</h2>
                <div className="mt-5 grid gap-4 sm:grid-cols-2">
                  <div className={`rounded-2xl p-4 ${isDarkMode ? "bg-slate-950" : "bg-slate-50"}`}>
                    <p className="text-sm text-slate-400">Collected this cycle</p>
                    <p className="mt-2 text-2xl font-extrabold text-emerald-600">₹24.6L</p>
                    <p className={`mt-1 text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                      Tuition and transport fee collection is stable.
                    </p>
                  </div>
                  <div className={`rounded-2xl p-4 ${isDarkMode ? "bg-slate-950" : "bg-slate-50"}`}>
                    <p className="text-sm text-slate-400">Outstanding dues</p>
                    <p className="mt-2 text-2xl font-extrabold text-amber-600">₹2.1L</p>
                    <p className={`mt-1 text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                      Reminder batch scheduled for tomorrow morning.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {prefs.showOperationsBoard && (
              <div className={`rounded-3xl border p-6 shadow-lg ${isDarkMode ? "border-slate-800 bg-slate-900 shadow-black/20" : "border-slate-200 bg-white shadow-slate-200/60"}`}>
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Operations Board</p>
                    <h2 className="mt-2 text-2xl font-bold">Today&apos;s school checklist</h2>
                  </div>
                  <div className={`rounded-full px-3 py-1 text-xs font-semibold ${isDarkMode ? "bg-slate-800 text-slate-300" : "bg-amber-50 text-amber-700"}`}>
                    {operations.length} open items
                  </div>
                </div>

                <div className="mt-5 space-y-4">
                  {operations.map((task) => (
                    <div
                      key={task.id}
                      className={`flex flex-col gap-4 rounded-2xl border p-4 md:flex-row md:items-center md:justify-between ${isDarkMode ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-slate-50"}`}
                    >
                      <div>
                        <div className="flex items-center gap-3">
                          <p className="font-bold">{task.title}</p>
                          <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${task.priority === "High" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}>
                            {task.priority}
                          </span>
                        </div>
                        <p className={`mt-1 text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                          {task.detail}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => completeOperation(task.id)}
                        className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700"
                      >
                        <FaCheckCircle className="text-xs" /> Mark done
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className={`rounded-3xl border p-6 shadow-lg ${isDarkMode ? "border-slate-800 bg-slate-900 shadow-black/20" : "border-slate-200 bg-white shadow-slate-200/60"}`}>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">School Health</p>
              <h2 className="mt-2 text-2xl font-bold">Operational signals</h2>
              <div className="mt-5 space-y-3">
                {[
                  { icon: FaSchool, label: "Campus readiness", status: "Stable across all blocks" },
                  { icon: FaCalendarAlt, label: "Academic calendar", status: "Next review on Friday" },
                  { icon: FaChartLine, label: "Performance reporting", status: "Weekly summary generated" },
                  { icon: FaExclamationTriangle, label: "Risk watch", status: "Transport notice pending" },
                ].map((item) => {
                  const Icon = item.icon;
                  return (
                    <div
                      key={item.label}
                      className={`flex items-center gap-4 rounded-2xl border p-4 ${isDarkMode ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-slate-50"}`}
                    >
                      <div className={`flex h-11 w-11 items-center justify-center rounded-2xl ${isDarkMode ? "bg-slate-800 text-emerald-400" : "bg-white text-emerald-600"}`}>
                        <Icon />
                      </div>
                      <div>
                        <p className="font-semibold">{item.label}</p>
                        <p className={`text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>{item.status}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      </div>

      {showCustomize && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 px-4 backdrop-blur-sm">
          <div className={`w-full max-w-xl rounded-3xl border p-6 shadow-2xl ${isDarkMode ? "border-slate-800 bg-slate-900 text-slate-100" : "border-slate-200 bg-white text-slate-900"}`}>
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Customize</p>
                <h2 className="mt-2 text-2xl font-bold">Choose school dashboard sections</h2>
              </div>
              <button
                type="button"
                onClick={() => setShowCustomize(false)}
                className={`rounded-full px-3 py-1 text-sm font-semibold transition ${isDarkMode ? "bg-slate-800 text-slate-200 hover:bg-slate-700" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
              >
                Close
              </button>
            </div>

            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              {preferenceOptions.map((option) => (
                <button
                  type="button"
                  key={option.key}
                  onClick={() => toggleDraftPref(option.key)}
                  className={`flex items-center justify-between rounded-2xl border px-4 py-3 text-left transition ${draftPrefs[option.key]
                    ? isDarkMode
                      ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200"
                      : "border-emerald-200 bg-emerald-50 text-emerald-700"
                    : isDarkMode
                      ? "border-slate-800 bg-slate-950 text-slate-300"
                      : "border-slate-200 bg-slate-50 text-slate-600"
                  }`}
                >
                  <span className="font-semibold">{option.label}</span>
                  <span className={`inline-flex h-6 w-10 items-center rounded-full p-1 transition ${draftPrefs[option.key] ? "bg-emerald-500" : isDarkMode ? "bg-slate-800" : "bg-slate-300"}`}>
                    <span className={`h-4 w-4 rounded-full bg-white transition ${draftPrefs[option.key] ? "translate-x-4" : "translate-x-0"}`} />
                  </span>
                </button>
              ))}
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={applyCustomization}
                className="rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-700"
              >
                Apply changes
              </button>
              <button
                type="button"
                onClick={resetCustomization}
                className={`rounded-full px-5 py-2.5 text-sm font-semibold transition ${isDarkMode ? "bg-slate-800 text-slate-200 hover:bg-slate-700" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
              >
                Reset defaults
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SchoolDashboard;
